import { useCallback, useRef, useState, type FormEvent } from 'react';
import { useAutofillSync } from '@/lib/useAutofillSync';
import { Button } from '@/components/ui/Button';
import { Field, PasswordField } from '@/components/ui/Field';
import { ErrorBlock, useCountdown } from '@/components/ui/Feedback';
import type { ApiError } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/AuthContext';
import { routes } from '@/routes/paths';
import { AuthLayout, AuthSwap, SessionLostNotice, authStyles as s } from './AuthLayout';
import { OAuthButtons } from './OAuthButtons';
import { useAuthSubmit } from './useAuthSubmit';
import { t } from '@/lib/i18n';

/**
 * The backend answers an unknown email and a wrong password with the same
 * 401 "Invalid credentials" — deliberately, so the form cannot be used to discover which
 * addresses have accounts. The UI keeps that property: one message, on the password field.
 */
const mapLoginError = (err: ApiError) =>
  err.isUnauthorized ? { field: 'password', message: t('auth.login.badCredentials') } : null;

export function Login() {
  const { login, sessionLost, clearSessionLost } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const { busy, fieldErrors, formError, rateLimitedFor, submit, clearRateLimit, setFieldErrors } =
    useAuthSubmit(mapLoginError);

  const secondsLeft = useCountdown(rateLimitedFor, clearRateLimit);

  const onAutofill = useCallback((values: Record<string, string>) => {
    if (values.email) setEmail((v) => v || (values.email as string));
    if (values.password) setPassword((v) => v || (values.password as string));
  }, []);
  useAutofillSync(formRef, onAutofill);

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      clearSessionLost();
      // Read straight from the DOM as a last resort — covers an autofill that landed
      // between the sync hook's poll and this submit.
      const form = formRef.current;
      const typedEmail =
        email.trim() ||
        (form?.elements.namedItem('email') as HTMLInputElement)?.value?.trim() ||
        '';
      const typedPassword =
        password || (form?.elements.namedItem('password') as HTMLInputElement)?.value || '';

      // Catch empties here rather than round-tripping. The endpoint allows 10 requests a
      // minute, and spending one to be told "email must be an email" is a waste of the
      // user's budget as well as ours.
      const missing: Record<string, string> = {};
      if (!typedEmail) missing.email = t('auth.missing.email');
      if (!typedPassword) missing.password = t('auth.missing.password');
      if (Object.keys(missing).length > 0) {
        setFieldErrors(missing);
        return;
      }

      // No navigate() here — RequireAnonymous sees the status flip and redirects, honouring
      // the `from` location the guard stored. Navigating here too would race with it.
      void submit(() => login(typedEmail, typedPassword));
    },
    [clearSessionLost, submit, setFieldErrors, login, email, password],
  );

  const blocked = secondsLeft != null && secondsLeft > 0;

  return (
    <AuthLayout title={t('auth.login.title')}>
      <form onSubmit={onSubmit} noValidate ref={formRef}>
        {(sessionLost || blocked || formError) && (
          <div className={s.topSlot}>
            {sessionLost && <SessionLostNotice reason={sessionLost} />}
            {blocked && (
              <ErrorBlock title={t('auth.tooManyAttempts')} secondsLeft={secondsLeft ?? 0}>
                {t('auth.waitAMinute')}
              </ErrorBlock>
            )}
            {formError && !blocked && (
              <ErrorBlock title={t('auth.login.failed')}>{formError}</ErrorBlock>
            )}
          </div>
        )}

        <Field
          label={t('auth.field.email')}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email ?? null}
          required
        />

        <PasswordField
          label={t('auth.field.password')}
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors.password ?? null}
          required
        />

        <button type="button" className={s.forgot} onClick={() => setForgotOpen((v) => !v)}>
          {t('auth.forgotPassword')}
        </button>
        {forgotOpen && (
          <p className={s.note}>
            {t('auth.noPasswordReset')}
          </p>
        )}

        <div className={s.actions}>
          {/* Deliberately not disabled on empty fields: a password manager can fill the
              form without React seeing it, and a dead button with no explanation is worse
              than a submit that comes back with an inline error. Only busy and the rate
              limit block it. */}
          <Button type="submit" block size="lg" busy={busy} disabled={blocked}>
            {t('auth.login.submit')}
          </Button>
        </div>

        <OAuthButtons />

        <AuthSwap
          question={t('auth.login.noAccount')}
          linkLabel={t('auth.register.title')}
          to={routes.register}
        />
      </form>
    </AuthLayout>
  );
}
